import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ArrowLeft, ArrowRight, CalendarClock, Clock, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import bookingModeSlotPreview from '@/assets/onboarding/booking-mode-slot.png';
import bookingModeTimePreview from '@/assets/onboarding/booking-mode-time.png';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface StepBookingModeProps {
  onComplete: (data: {
    bookingMode: 'slot' | 'time';
    bookingIntervalMinutes: 15 | 30 | 60;
    reservationDurationMinutes: number;
  }) => void;
  onBack: () => void;
  onSkip: () => void;
}

const INTERVALS = [
  { value: 15, label: 'Toutes les 15 minutes' },
  { value: 30, label: 'Toutes les 30 minutes' },
  { value: 60, label: 'Toutes les heures' },
];

const DURATIONS = [
  { value: 60, label: '1 heure' },
  { value: 75, label: '1h15' },
  { value: 90, label: '1h30' },
  { value: 105, label: '1h45' },
  { value: 120, label: '2 heures' },
  { value: 135, label: '2h15' },
  { value: 150, label: '2h30' },
  { value: 165, label: '2h45' },
  { value: 180, label: '3 heures' },
];

export function StepBookingMode({ onComplete, onBack, onSkip }: StepBookingModeProps) {
  const [bookingMode, setBookingMode] = useState<'slot' | 'time'>('slot');
  const [bookingIntervalMinutes, setBookingIntervalMinutes] = useState<15 | 30 | 60>(30);
  const [reservationDurationMinutes, setReservationDurationMinutes] = useState<number>(90);

  const handleSubmit = () => {
    onComplete({
      bookingMode,
      bookingIntervalMinutes,
      reservationDurationMinutes,
    });
  };

  // Generate example times for preview
  const generateExampleTimes = () => {
    const times: string[] = [];
    let hour = 12;
    let minute = 0;
    for (let i = 0; i < 4; i++) {
      times.push(`${hour}h${minute === 0 ? '00' : minute}`);
      minute += bookingIntervalMinutes;
      if (minute >= 60) {
        minute -= 60;
        hour++;
      }
    }
    return times;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <CalendarClock className="w-6 h-6 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">Mode de réservation</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Comment vos clients choisiront-ils leur horaire ?
        </p>
      </div>

      {/* Mode selection */}
      <div className="space-y-3">
        {/* Mode Créneaux */}
        <button
          type="button"
          onClick={() => setBookingMode('slot')}
          className={cn(
            "w-full p-4 rounded-lg border-2 text-left transition-all",
            bookingMode === 'slot'
              ? "border-primary bg-primary/5"
              : "border-border hover:border-muted-foreground/50"
          )}
        >
          <div className="flex items-start gap-3">
            <div className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
              bookingMode === 'slot' ? "bg-primary/20" : "bg-muted"
            )}>
              <Clock className={cn(
                "w-5 h-5",
                bookingMode === 'slot' ? "text-primary" : "text-muted-foreground"
              )} />
            </div>
            <div className="flex-1">
              <h3 className="font-medium">Mode Créneaux</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Les clients réservent au <strong>début de chaque service</strong>
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Ex: Service midi à 12h → Réservations à 12h uniquement
              </p>
              {/* Aperçu client - Mode Créneaux */}
              <div className="mt-3 rounded-md overflow-hidden border shadow-sm">
                <img 
                  src={bookingModeSlotPreview} 
                  alt="Aperçu du mode créneaux" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </button>

        {/* Mode Horaires libres */}
        <button
          type="button"
          onClick={() => setBookingMode('time')}
          className={cn(
            "w-full p-4 rounded-lg border-2 text-left transition-all",
            bookingMode === 'time'
              ? "border-primary bg-primary/5"
              : "border-border hover:border-muted-foreground/50"
          )}
        >
          <div className="flex items-start gap-3">
            <div className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
              bookingMode === 'time' ? "bg-primary/20" : "bg-muted"
            )}>
              <CalendarClock className={cn(
                "w-5 h-5",
                bookingMode === 'time' ? "text-primary" : "text-muted-foreground"
              )} />
            </div>
            <div className="flex-1">
              <h3 className="font-medium">Mode Horaires libres</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Les clients choisissent une <strong>heure précise</strong> dans vos services
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Ex: Service midi 12h-14h30 → Réservations à 12h, 12h30, 13h...
              </p>
              {/* Aperçu client - Mode Horaires libres */}
              <div className="mt-3 rounded-md overflow-hidden border shadow-sm">
                <img 
                  src={bookingModeTimePreview} 
                  alt="Aperçu du mode horaires libres" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Time mode configuration */}
      {bookingMode === 'time' && (
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg border">
          <div className="space-y-2">
            <Label htmlFor="interval">Intervalle entre les réservations</Label>
            <Select
              value={bookingIntervalMinutes.toString()}
              onValueChange={(value) => setBookingIntervalMinutes(parseInt(value) as 15 | 30 | 60)}
            >
              <SelectTrigger id="interval">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTERVALS.map((interval) => (
                  <SelectItem key={interval.value} value={interval.value.toString()}>
                    {interval.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">Durée d'une réservation</Label>
            <Select
              value={reservationDurationMinutes.toString()}
              onValueChange={(value) => setReservationDurationMinutes(parseInt(value))}
            >
              <SelectTrigger id="duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATIONS.map((duration) => (
                  <SelectItem key={duration.value} value={duration.value.toString()}>
                    {duration.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Preview */}
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-2">
              Aperçu des horaires proposés :
            </p>
            <div className="flex flex-wrap gap-2">
              {generateExampleTimes().map((time, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md font-medium"
                >
                  {time}
                </span>
              ))}
              <span className="text-xs text-muted-foreground self-center">...</span>
            </div>
          </div>
        </div>
      )}

      {/* Info alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          {bookingMode === 'slot' ? (
            <>
              Le <strong>mode créneaux</strong> est idéal pour les restaurants avec des services définis.
              Les clients réservent au début de chaque créneau que vous avez configuré.
            </>
          ) : (
            <>
              Le <strong>mode horaires libres</strong> offre plus de flexibilité. 
              Parfait pour les établissements avec un flux continu de clients.
            </>
          )}
        </AlertDescription>
      </Alert>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button variant="outline" onClick={onBack} className="flex-1">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <Button variant="ghost" onClick={onSkip}>
          Passer
        </Button>
        <Button onClick={handleSubmit} className="flex-1">
          Continuer
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
